import React from "react";

import { isAxiosError } from "axios";

import { useDeleteCredentialById } from "src/api/agents/credentials";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import type { CredentialItem } from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";

type Props = {
  credential?: CredentialItem;
  onClose: () => void;
};

export const DeleteCredentialModal: React.FC<Props> = ({
  credential,
  onClose,
}) => {
  const deleteCredential = useDeleteCredentialById();

  const handleDelete = async () => {
    if (!credential) return;

    try {
      await deleteCredential.mutateAsync({ credentialId: credential.id });
      toastActions.success({
        title: "Credential deleted",
        description: `"${credential.name}" credentials have been removed.`,
      });
      onClose();
    } catch (error) {
      logger.error(`Error deleting credential: ${error}`);

      let description =
        "An error occurred while deleting the credential. Please try again.";
      if (isAxiosError(error) && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          description = detail
            .map((item: unknown) =>
              typeof item === "object" && item !== null && "msg" in item
                ? (item as { msg: string }).msg
                : String(item),
            )
            .join(". ");
        } else {
          description = String(detail);
        }
      }

      toastActions.failure({
        title: "Failed to delete credential",
        description,
      });
    }
  };

  const credentialName = credential?.name ?? "Unknown";

  return (
    <TextConfirmationModal
      challengeText={credentialName}
      description={`This will permanently delete the "${credentialName}" credentials. AI agents will no longer be able to use them.`}
      label={`Type "${credentialName}" to confirm`}
      open={credential !== undefined}
      title={`Delete "${credentialName}" credentials?`}
      onClose={onClose}
      onConfirm={handleDelete}
    />
  );
};
